import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, Calendar, CheckCircle, XCircle, MapPin, FileText, Pencil, Power, Trash2 } from 'lucide-react';
import type { Cliente } from './ClientesTable';

interface ClienteDetailsDrawerProps {
  isOpen: boolean;
  cliente: Cliente | null;
  onClose: () => void;
  onEdit?: (cliente: Cliente) => void;
  onToggleStatus?: (cliente: Cliente) => void;
  onDelete?: (cliente: Cliente) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const ClienteDetailsDrawer: React.FC<ClienteDetailsDrawerProps> = ({
  isOpen,
  cliente,
  onClose,
  onEdit,
  onToggleStatus,
  onDelete,
  canEdit = false,
  canDelete = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentCliente, setCurrentCliente] = useState<Cliente | null>(null);

  useEffect(() => {
    if (isOpen && cliente) {
      setCurrentCliente(cliente);
      // Pequeño delay para asegurar que el componente se monte antes de la animación
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, cliente]);

  // Limpiar el currentCliente después de la animación de salida
  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentCliente(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (nombres: string, apellidos: string) => {
    const firstName = nombres.split(' ')[0];
    const firstLastName = apellidos.split(' ')[0];
    return `${firstName.charAt(0)}${firstLastName.charAt(0)}`.toUpperCase();
  };

  if (!isOpen && !currentCliente) return null;
  if (!currentCliente) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay invisible para cerrar al hacer clic fuera */}
        <div 
          className="absolute inset-0 cursor-pointer bg-black/20 transition-opacity duration-300"
          style={{ opacity: isVisible ? 1 : 0 }}
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Detalles del Cliente
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar y nombre */}
              <div className="text-center py-6 bg-gradient-to-b from-emerald-50 to-white">
                <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 flex items-center justify-center mb-3 shadow-lg">
                  <span className="text-white text-2xl font-bold">
                    {getInitials(currentCliente.nombres, currentCliente.apellidos)}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {currentCliente.nombres} {currentCliente.apellidos}
                </h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  currentCliente.estado === 'activo' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {currentCliente.estado === 'activo' ? (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-1" />
                  )}
                  {currentCliente.estado}
                </span>
              </div>

              {/* Información */}
              <div className="px-6 py-6 space-y-6">
                {/* Información de Contacto */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                    <User className="w-5 h-5 mr-2 text-emerald-600" />
                    Información de Contacto
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Mail className="w-5 h-5 text-emerald-500 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Correo electrónico</p>
                        <p className="text-sm font-medium text-gray-900">{currentCliente.email || 'No especificado'}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Phone className="w-5 h-5 text-emerald-500 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Teléfono</p>
                        <p className="text-sm font-medium text-gray-900">{currentCliente.telefono || 'No especificado'}</p>
                      </div>
                    </div>
                    {currentCliente.numeroDocumento && (
                      <div className="flex items-start">
                        <FileText className="w-5 h-5 text-emerald-500 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Documento</p>
                          <p className="text-sm font-medium text-gray-900">{currentCliente.numeroDocumento}</p>
                        </div>
                      </div>
                    )}
                    {currentCliente.direccion && (
                      <div className="flex items-start">
                        <MapPin className="w-5 h-5 text-emerald-500 mt-0.5 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Dirección</p>
                          <p className="text-sm font-medium text-gray-900">{currentCliente.direccion}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Información del Sistema */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-emerald-600" />
                    Información del Sistema
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-500">ID del Cliente</p>
                      <p className="font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded mt-1">{currentCliente.id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Fecha de registro</p>
                      <p className="font-medium text-gray-900">{formatDate(currentCliente.creadoEn)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Última actualización</p>
                      <p className="font-medium text-gray-900">{formatDate(currentCliente.actualizadoEn)}</p>
                    </div>
                  </div>
                </div>

                {/* Consentimientos */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Consentimientos
                  </h4>
                  <div className="flex items-center bg-gray-50 p-3 rounded-lg">
                    {currentCliente.consentimientoDatos ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mr-2" />
                    )}
                    <span className="text-sm text-gray-700">
                      {currentCliente.consentimientoDatos 
                        ? 'Ha consentido el tratamiento de sus datos personales' 
                        : 'No ha consentido el tratamiento de sus datos personales'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-row gap-2">
                {canEdit && (
                  <>
                    <button
                      onClick={() => { currentCliente && onEdit?.(currentCliente); }}
                      className="flex-1 flex items-center justify-center space-x-1 bg-blue-600 text-white px-3 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                    >
                      <Pencil className="w-4 h-4" />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={() => { currentCliente && onToggleStatus?.(currentCliente); }}
                      className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                        currentCliente?.estado === 'activo'
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      <Power className="w-4 h-4" />
                      <span>{currentCliente?.estado === 'activo' ? 'Desactivar' : 'Activar'}</span>
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    onClick={() => { currentCliente && onDelete?.(currentCliente); }}
                    className="flex-1 flex items-center justify-center space-x-1 bg-red-600 text-white px-3 py-2.5 rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Eliminar</span>
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="flex-1 flex items-center justify-center space-x-1 bg-gray-200 text-gray-800 px-3 py-2.5 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm"
                >
                  <X className="w-4 h-4" />
                  <span>Cerrar</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClienteDetailsDrawer;
